// src/components/Flow.js
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BezierEdge,
} from 'reactflow';
import { supabase } from '../supabaseClient';
import { nodeManager } from './nodes/NodeManager';
import { NodePanel } from './nodes/NodePanel';
import 'reactflow/dist/style.css';

// 注册边类型
const edgeTypes = {
  bezier: BezierEdge,
};

// 初始数据
const initialNodes = [
  {
    id: '1',
    type: 'custom',
    data: { label: '开始项目', icon: '🚀' },
    position: { x: 250, y: 25 },
    className: 'start-node',
  },
  {
    id: '2',
    type: 'custom',
    data: { label: '需求分析', icon: '📋' },
    position: { x: 250, y: 125 },
  },
  {
    id: '3',
    type: 'entity',
    data: nodeManager.getDefaultData('entity'),
    position: { x: 100, y: 225 },
  },
  {
    id: '4',
    type: 'decision',
    data: nodeManager.getDefaultData('decision'),
    position: { x: 250, y: 325 },
  },
  {
    id: '5',
    type: 'custom',
    data: { label: '开发实现', icon: '💻' },
    position: { x: 100, y: 425 },
  },
  {
    id: '6',
    type: 'entity',
    data: { 
      label: '产品数据', 
      entityType: 'Table',
      attributes: [
        { name: 'id', type: 'int' },
        { name: 'title', type: 'string' },
        { name: 'price', type: 'decimal' }
      ]
    },
    position: { x: 400, y: 225 },
  },
  {
    id: '7',
    type: 'custom',
    data: { label: '测试验收', icon: '🧪' },
    position: { x: 250, y: 525 },
  },
  {
    id: '8',
    type: 'custom',
    data: { label: '项目完成', icon: '✅' },
    position: { x: 250, y: 625 },
    className: 'end-node',
  },
];

const initialEdges = [
  { id: 'e1-2', source: '1', target: '2', label: '启动' },
  { id: 'e2-3', source: '2', target: '3', label: '分析数据' },
  { id: 'e2-6', source: '2', target: '6', label: '分析产品' },
  { id: 'e3-4', source: '3', target: '4', label: '提交评审' },
  { id: 'e6-4', source: '6', target: '4', label: '产品评审' },
  { id: 'e4-5', source: '4', target: '5', label: '通过' },
  { id: 'e5-7', source: '5', target: '7', label: '开发完成' },
  { id: 'e7-8', source: '7', target: '8', label: '测试通过' },
];

function Flow() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const reactFlowWrapper = useRef(null);

  const isEditAllowed = process.env.REACT_APP_ALLOW_EDIT === 'true';

  // 组织节点编辑处理
  const handleOrgNodeEdit = useCallback((nodeId, nodeData) => {
    const name = prompt('请输入姓名:', nodeData.name || '姓名') || nodeData.name;
    const title = prompt('请输入职位:', nodeData.title || '职位') || nodeData.title;
    const department = prompt('请输入部门:', nodeData.department || '部门') || nodeData.department;
    
    if (name !== null) {
      // 根据姓名设置默认头像映射
      const defaultAvatarMap = {
        '张三': '/images/avatars/avatar1.png',
        '李四': '/images/avatars/avatar2.png',
        '王五': '/images/avatars/avatar3.png'
      };
      
      let avatar = nodeData.avatar;
      let avatarRepoPath = nodeData.avatarRepoPath;
      
      // 如果当前是Blob URL但没有repo路径，设置默认头像
      if (avatar && avatar.startsWith('blob:') && !avatarRepoPath) {
        avatar = defaultAvatarMap[name] || '/images/avatars/default.png';
        avatarRepoPath = avatar;
      }
      
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { 
            ...n, 
            data: { 
              ...n.data, 
              name, 
              title, 
              department, 
              avatar,
              avatarRepoPath: avatarRepoPath || avatar
            } 
          } : n
        )
      );
    }
  }, [setNodes]);

  const handleImageUpload = useCallback((nodeId, file) => {
    return new Promise((resolve, reject) => {
      if (!isEditAllowed) {
        reject(new Error('编辑模式已禁用'));
        return;
      }

      // 检查文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('❌ 只支持 JPG, PNG, GIF, WebP 格式的图片');
        reject(new Error('不支持的图片格式'));
        return;
      }

      // 检查文件大小（限制为 2MB）
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('❌ 图片大小不能超过 2MB');
        reject(new Error('图片过大'));
        return;
      }

      // 使用原始文件名（清理特殊字符）
      const originalName = file.name;
      const safeFileName = originalName
        .replace(/[^a-zA-Z0-9\u4e00-\u9fa5.-]/g, '_') // 替换特殊字符为下划线
        .replace(/_+/g, '_') // 合并连续下划线
        .replace(/^_|_$/g, ''); // 去除首尾下划线
      
      const repoImagePath = `/images/uploaded/${safeFileName}`;

      // 创建本地预览
      const localImageUrl = URL.createObjectURL(file);
      
      // 检查文件名是否已存在（模拟检查）
      const checkFileName = () => {
        return new Promise((resolveCheck) => {
          alert(`📁 图片上传信息：
          
  图片文件名: ${safeFileName}
  保存路径: public/images/uploaded/${safeFileName}
  访问路径: ${repoImagePath}

  请确保将图片文件保存到指定目录。`);
          
          resolveCheck(true);
        });
      };

      checkFileName().then((isValid) => {
        if (isValid) {
          // 保存到 Supabase（使用原始文件名路径）
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId ? { 
                ...n, 
                data: { 
                  ...n.data, 
                  avatar: localImageUrl,        // 临时预览
                  avatarRepoPath: repoImagePath, // 永久repo路径
                  avatarFileName: safeFileName   // 记录文件名
                } 
              } : n
            )
          );

          resolve({ 
            url: localImageUrl, 
            repoPath: repoImagePath,
            fileName: safeFileName,
            message: `图片路径已记录，请将文件保存为: ${safeFileName}` 
          });
        } else {
          URL.revokeObjectURL(localImageUrl); // 清理Blob URL
          reject(new Error('文件名检查失败'));
        }
      });
    });
  }, [setNodes, isEditAllowed]);

  // 保存数据到 Supabase
  const saveFlowToSupabase = useCallback(async (nodes, edges) => {
    if (!isEditAllowed) {
      console.log('保存被阻止：非编辑模式');
      return;
    }

    try {
      console.log('正在保存数据到Supabase...');
      const { data, error } = await supabase
        .from('flow_data')
        .upsert(
          { 
            id: 1, 
            nodes: nodes || [], 
            edges: edges || [], 
            updated_at: new Date().toISOString() 
          },
          { onConflict: 'id' }
        );

      if (error) {
        console.error('保存数据错误:', error);
        throw error;
      } else {
        console.log('数据保存成功', new Date().toLocaleTimeString());
        return data;
      }
    } catch (error) {
      console.error('保存异常:', error);
      throw error;
    }
  }, [isEditAllowed]);

  // 从 Supabase 加载数据
  const loadFlowFromSupabase = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('flow_data')
        .select('*')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('加载数据错误:', error);
        return null;
      }

      // 处理图片路径：优先使用repo路径
      if (data && data.nodes) {
        data.nodes = data.nodes.map(node => {
          if (node.type === 'org' && node.data) {
            const nodeData = node.data;
            
            // 图片路径优先级：1. repo路径 2. 现有avatar 3. 默认映射 4. 默认图片
            let finalAvatar = '/images/avatars/default.png';
            
            if (nodeData.avatarRepoPath) {
              // 优先使用记录的repo路径
              finalAvatar = nodeData.avatarRepoPath;
            } else if (nodeData.avatar && !nodeData.avatar.startsWith('blob:')) {
              // 使用现有的非Blob URL
              finalAvatar = nodeData.avatar;
            } else if (nodeData.name) {
              // 根据姓名映射到预定义图片
              const nameMap = {
                '张三': '/images/avatars/avatar1.png',
                '李四': '/images/avatars/avatar2.png',
                '王五': '/images/avatars/avatar3.png'
              };
              finalAvatar = nameMap[nodeData.name] || finalAvatar;
            }
            
            return {
              ...node,
              data: {
                ...nodeData,
                avatar: finalAvatar,
                // 确保avatarRepoPath有值
                avatarRepoPath: nodeData.avatarRepoPath || finalAvatar
              }
            };
          }
          return node;
        });
      }

      return data;
    } catch (error) {
      console.error('加载异常:', error);
      return null;
    }
  }, []);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const savedData = await loadFlowFromSupabase();
      
      if (savedData && savedData.nodes && savedData.edges) {
        setNodes(savedData.nodes);
        setEdges(savedData.edges);
      } else {
        setNodes(initialNodes);
        setEdges(initialEdges);
      }
      setIsLoading(false);
    };

    loadData();
  }, [loadFlowFromSupabase, setNodes, setEdges]);

  // 自动保存
  useEffect(() => {
    if (isLoading || !isEditAllowed) return;

    const timeoutId = setTimeout(() => {
      saveFlowToSupabase(nodes, edges)
        .then(() => console.log('保存成功'))
        .catch(err => console.error('保存失败:', err));
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, isLoading, isEditAllowed, saveFlowToSupabase]);

  // 连接节点
  const onConnect = useCallback((params) => {
    if (!isEditAllowed) return;
    
    const newEdges = addEdge(params, edges);
    setEdges(newEdges);
  }, [edges, setEdges, isEditAllowed]);

  // 节点双击事件
  const onNodeDoubleClick = useCallback((event, node) => {
    if (!isEditAllowed) return;
    
    let updatedData = { ...node.data };
    
    switch (node.type) {
      case 'text':
        updatedData.title = prompt('请输入标题:', node.data.title || '文本节点') || node.data.title;
        updatedData.content = prompt('请输入内容:', node.data.content || '') || node.data.content;
        break;
        
      case 'document':
        updatedData.title = prompt('请输入文档标题:', node.data.title || '文档') || node.data.title;
        updatedData.description = prompt('请输入文档描述:', node.data.description || '') || node.data.description;
        break;
        
      case 'note':
        updatedData.text = prompt('请输入注释内容:', node.data.text || '注释内容...') || node.data.text;
        break;
        
      case 'entity':
        updatedData.label = prompt('请输入实体名称:', node.data.label || '新实体') || node.data.label;
        updatedData.entityType = prompt('请输入实体类型:', node.data.entityType || 'Table') || node.data.entityType;
        break;
        
      case 'decision':
        updatedData.label = prompt('请输入决策条件:', node.data.label || '判断条件？') || node.data.label;
        break;

      case 'org':
        if (confirm('是否上传头像图片？\n\n确定：选择图片文件（请确保图片已保存在public/images/uploaded/目录）\n取消：仅编辑文本信息')) {
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'image/*';
          fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
              try {
                const result = await handleImageUpload(node.id, file);
                alert(`✅ ${result.message}`);
              } catch (error) {
                if (error.message !== '不支持的图片格式' && error.message !== '图片过大') {
                  alert('❌ 图片处理失败，请重试');
                }
              }
            }
          };
          fileInput.click();
        } else {
          handleOrgNodeEdit(node.id, node.data);
        }
        return;
        
      default:
        updatedData.label = prompt('请输入节点标签:', node.data.label || '新节点') || node.data.label;
        if (node.data.icon !== undefined) {
          updatedData.icon = prompt('请输入图标:', node.data.icon || '📝') || node.data.icon;
        }
    }

    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id ? { ...n, data: updatedData } : n
      )
    );
  }, [setNodes, isEditAllowed, handleImageUpload, handleOrgNodeEdit]);

  // 节点删除
  const handleNodeDelete = useCallback((nodesToDelete) => {
    nodesToDelete.forEach((node) => {
      if (node.type === 'org' && node.data.avatar) {
        URL.revokeObjectURL(node.data.avatar);
      }
    });
    
    setNodes((nds) => nds.filter((node) => !node.selected));
    setEdges((eds) => eds.filter((edge) => !edge.selected));
  }, [setNodes, setEdges]);

  // 键盘事件
  const onKeyDown = useCallback((event) => {
    if (!isEditAllowed) return;
    
    if (event.key === 'Delete') {
      const selectedNodes = nodes.filter(node => node.selected);
      handleNodeDelete(selectedNodes);
    }
  }, [nodes, handleNodeDelete, isEditAllowed]);

  // 拖拽事件
  const onDragStart = useCallback((event, nodeType) => {
    if (!isEditAllowed) return;
    
    event.dataTransfer.setData('application/reactflow/nodeType', nodeType.type);
    event.dataTransfer.setData('application/reactflow/nodeData', JSON.stringify(nodeType.defaultData));
    event.dataTransfer.effectAllowed = 'move';
  }, [isEditAllowed]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = isEditAllowed ? 'move' : 'none';
  }, [isEditAllowed]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      
      if (!isEditAllowed) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow/nodeType');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow/nodeData'));
      
      if (typeof type === 'undefined' || !type) return;

      const position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };

      const newNode = {
        id: `${nodes.length + 1}-${Date.now()}`,
        type,
        position,
        data: nodeData,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [nodes.length, setNodes, isEditAllowed]
  );

  // 节点变化处理
  const handleNodesChange = useCallback((changes) => {
    if (!isEditAllowed) return;
    onNodesChange(changes);
  }, [onNodesChange, isEditAllowed]);

  const handleEdgesChange = useCallback((changes) => {
    if (!isEditAllowed) return;
    onEdgesChange(changes);
  }, [onEdgesChange, isEditAllowed]);

  // 重置功能
  const resetFlow = useCallback(async () => {
    if (!isEditAllowed) return;
    
    setNodes(initialNodes);
    setEdges(initialEdges);
    if (isEditAllowed) {
      await saveFlowToSupabase(initialNodes, initialEdges);
    }
  }, [setNodes, setEdges, isEditAllowed, saveFlowToSupabase]);

  // 导出导入功能
  const exportData = useCallback(() => {
    const flowData = { 
      nodes, 
      edges,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    const dataStr = JSON.stringify(flowData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `flowchart-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [nodes, edges]);

  const importData = useCallback((event) => {
    const fileReader = new FileReader();
    fileReader.readAsText(event.target.files[0], "UTF-8");
    fileReader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (data.nodes && Array.isArray(data.nodes) && data.edges && Array.isArray(data.edges)) {
          setNodes(data.nodes);
          setEdges(data.edges);
          
          if (isEditAllowed) {
            saveFlowToSupabase(data.nodes, data.edges);
          }
          
          alert('导入成功！');
        } else {
          alert('文件格式不正确，请选择正确的流程图文件。');
        }
      } catch (error) {
        alert('导入失败：文件格式错误');
        console.error('导入错误:', error);
      }
      
      event.target.value = '';
    };
  }, [setNodes, setEdges, isEditAllowed, saveFlowToSupabase]);

  // 使用节点管理器获取节点组件
  const nodeTypes = useMemo(() => 
    nodeManager.getNodeComponents({ onEdit: handleOrgNodeEdit }), 
    [handleOrgNodeEdit]
  );

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-icon">⏳</div>
          <div>加载流程图中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow-container">
      <NodePanel onDragStart={onDragStart} isEditAllowed={isEditAllowed} />
      
      <div className="flow-wrapper" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={isEditAllowed ? handleNodesChange : undefined}
          onEdgesChange={isEditAllowed ? handleEdgesChange : undefined}
          onConnect={isEditAllowed ? onConnect : undefined}
          onKeyDown={isEditAllowed ? onKeyDown : undefined}
          onNodeDoubleClick={isEditAllowed ? onNodeDoubleClick : undefined}
          onDrop={onDrop}
          onDragOver={onDragOver}
          deleteKeyCode={isEditAllowed ? "Delete" : undefined}
          nodesDraggable={isEditAllowed}
          elementsSelectable={isEditAllowed}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          panOnScroll={true}
          zoomOnScroll={true}
          panOnDrag={true}
          zoomOnPinch={true}
          selectionOnDrag={isEditAllowed}
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>

      <div className="control-panel">
        {isEditAllowed ? (
          <>
            <div className="toolbar">
              <button onClick={exportData} className="toolbar-btn export-btn">
                📤 导出
              </button>
              <label htmlFor="import-file" className="toolbar-btn import-btn">
                📥 导入
                <input
                  id="import-file"
                  type="file"
                  accept=".json"
                  onChange={importData}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            <div className="mode-badge edit-mode">
              🔧 编辑模式
            </div>
          </>
        ) : (
          <div className="mode-badge read-only">
            👀 只读模式
          </div>
        )}
        
        <div className="data-source">
          数据来源: Supabase
        </div>
      </div>
    </div>
  );
}

export default Flow;